import { addDays, parseISO, format } from 'date-fns'
import type {
  PropertyDetailsResponse,
  ExpenseBenchmarksResponse,
  Distribution,
  Similarities,
  Availability
} from './clients'
import {
  getUnitsInDateRange,
  estimateUnitMix,
  occupancyOverTime,
  getLatestPricesByFilter,
  getBestLeaseTermPlan,
  defaultBestLeaseTermRentBasis,
  type BestLeaseTermRentBasisValue
} from './utils'

function withFloorplanNames(property: PropertyDetailsResponse): PropertyDetailsResponse {
  return {
    ...property,
    building_availability: property.building_availability.map((unit) => ({
      ...unit,
      floorplan_name:
        unit.floorplan_name ?? `${unit.bed ?? 0}BD / ${unit.bath ?? 0}BR - ${unit.sqft ?? 0} sqft`
    }))
  }
}

const reviewCountCategories = [
  'location',
  'amenities_and_features',
  'staff_and_management',
  'cleanliness_and_maintenance',
  'value'
] as const

const normalizeReviewCounts = (counts?: Record<string, number> | null) =>
  Object.fromEntries(
    reviewCountCategories.map((category) => [category, counts?.[category] ?? null])
  )

const normalizeReviews = (reviews: PropertyDetailsResponse['review_analysis']) => {
  return {
    avg_score: reviews?.avg_score ?? null,
    count_reviews: reviews?.count_reviews ?? null,
    positive_counts: normalizeReviewCounts(reviews?.positive_counts),
    negative_counts: normalizeReviewCounts(reviews?.negative_counts)
  }
}

export const availabilityAvg = (
  units: Availability[],
  getter: (unit: Availability) => number | null,
  filter: (unit: Availability) => boolean = () => true
): number | null => {
  // Compute the average of a given attribute for a list of units
  const filteredAttrs = units
    .filter(filter)
    .map(getter)
    .filter((_): _ is number => _ !== null)

  if (filteredAttrs.length === 0) {
    return null
  }

  return filteredAttrs.reduce((acc, attr) => acc + attr, 0) / filteredAttrs.length
}

export const avgRent = (units: Availability[]): number | null => {
  return availabilityAvg(units, (unit) => unit.price)
}

export const avgEffectiveRent = (units: Availability[]): number | null => {
  return availabilityAvg(units, (unit) => unit.effective_price)
}

export const avgSqft = (units: Availability[]): number | null => {
  return availabilityAvg(units, (unit) => unit.sqft)
}

export const avgRentPerSqft = (units: Availability[]): number | null => {
  const filteredUnits = units.filter((unit) => unit.price !== null && unit.sqft !== null)

  const totalPrice = filteredUnits
    .map((unit) => unit.price as number)
    .reduce((acc, val) => acc + val, 0)
  const totalSqft = filteredUnits
    .map((unit) => unit.sqft as number)
    .reduce((acc, val) => acc + val, 0)

  return totalSqft > 0 ? totalPrice / totalSqft : null
}

export const avgEffRentPerSqft = (units: Availability[]): number | null => {
  const filteredUnits = units.filter((unit) => unit.effective_price !== null && unit.sqft !== null)

  const totalEffectivePrice = filteredUnits
    .map((unit) => unit.effective_price as number)
    .reduce((acc, val) => acc + val, 0)
  const totalSqft = filteredUnits
    .map((unit) => unit.sqft as number)
    .reduce((acc, val) => acc + val, 0)

  return totalSqft > 0 ? totalEffectivePrice / totalSqft : null
}

export const computeFinancialBreakdown = (
  amount: Distribution | null | undefined,
  numberUnits: number | null,
  egi: Distribution | null | undefined,
  gpr: Distribution | null | undefined
): {
  lower: number | null
  amount: number | null
  upper: number | null
  per_unit: number | null
  percent_egi: number | null
  percent_gpr: number | null
} => {
  return {
    lower: amount?.lower || null,
    amount: amount?.predicted || null,
    upper: amount?.upper || null,
    per_unit: amount?.predicted && numberUnits ? amount.predicted / numberUnits : null,
    percent_egi: amount?.predicted && egi?.predicted ? amount.predicted / egi.predicted : null,
    percent_gpr: amount?.predicted && gpr?.predicted ? amount.predicted / gpr.predicted : null
  }
}

interface Accessor {
  label: string // label for the column
  accessor: (unit: Availability) => number | null // accessor for the value
}

// computes the average of some values for each combination of bed/bath
// returns, for example: { "1BR-1BA": { effective_price: 1000, price: 1200, sqft: 800, count: 10 }, ... }
// always add the `count` that is the number of units for each combination
export const avgPerUnitType = (
  units: Availability[],
  recencyDays: number,
  accessors: Accessor[]
) => {
  // we build the limit for exit_market, such as 90 days => "2023-06-10", etc
  const last90Days = addDays(new Date(), -recencyDays)

  // we build the map of all the unit types
  const unitTypes: { [key: string]: { [key: string]: number[] } } = {}
  for (const unit of units) {
    if (unit.exit_market === null) {
      return
    }
    const key = `${unit.bed}BR-${unit.bath}BA`
    if (!unitTypes[key]) {
      unitTypes[key] = {}
      for (const _ of accessors) {
        unitTypes[key][_.label] = []
      }
    }
    for (const _ of accessors) {
      if (unit[_.label as keyof Availability] !== null && parseISO(unit.created_on) >= last90Days) {
        unitTypes[key][_.label].push(unit[_.label as keyof Availability] as number)
      }
    }
  }

  // we compute the averages for each unit type
  const averages: { [key: string]: { [key: string]: number | null } } = {}
  for (const key of Object.keys(unitTypes)) {
    averages[key] = {}
    for (const _ of accessors) {
      if (unitTypes[key][_.label].length === 0) {
        averages[key][_.label] = null
      } else {
        averages[key][_.label] =
          unitTypes[key][_.label].reduce((acc, val) => acc + val, 0) /
          unitTypes[key][_.label].length
      }
    }
    // return the max count of all the accessors
    averages[key]['count'] = Math.max(...accessors.map((_) => unitTypes[key][_.label].length))
  }

  return averages
}

export const avgMarketPerBed = (beds: number, units: Availability[]): number | null => {
  return availabilityAvg(
    units,
    (unit) => unit.price,
    (unit) => unit.bed === beds && unit.price !== null
  )
}

export const avgSqftPerBed = (beds: number, units: Availability[]): number | null => {
  return availabilityAvg(
    units,
    (unit) => unit.sqft,
    (unit) => unit.bed === beds && unit.sqft !== null
  )
}

export const avgEffectivePerBed = (beds: number, units: Availability[]): number | null => {
  return availabilityAvg(
    units,
    (unit) => unit.effective_price,
    (unit) => unit.bed === beds && unit.effective_price !== null
  )
}

// format a number as a score out of 10 (string)
export const qualityFromFloat = (quality: number | null): string | null => {
  if (quality === null || quality === undefined) {
    return '-'
  }
  return `${Math.round(quality * 10)}/10`
}

export const excelDate = (date: string): number => {
  // format a date as a number for excel, such as 2023-06-10 => 46800
  return new Date(date).getTime() / 1000 / 60 / 60 / 24 + 25569
}

const formatFullAddress = (prop: {
  street_address: string
  city: string
  state: string
  zip_code: string
}): string => {
  return `${prop.street_address}, ${prop.city}, ${prop.state} ${prop.zip_code}`
}

function computeAvgGrid(
  units30day: Availability[],
  units60day: Availability[],
  units90day: Availability[],
  accessor: (unit: Availability) => number | null
): { [key: string]: number | null } {
  return {
    all_30day: availabilityAvg(units30day, accessor),
    all_60day: availabilityAvg(units60day, accessor),
    all_90day: availabilityAvg(units90day, accessor),
    studio_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 0),
    one_bed_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 1),
    two_bed_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 2),
    three_bed_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 3),
    four_bed_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 4),
    five_bed_30day: availabilityAvg(units30day, accessor, (unit) => unit.bed === 5),
    studio_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 0),
    one_bed_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 1),
    two_bed_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 2),
    three_bed_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 3),
    four_bed_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 4),
    five_bed_60day: availabilityAvg(units60day, accessor, (unit) => unit.bed === 5),
    studio_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 0),
    one_bed_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 1),
    two_bed_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 2),
    three_bed_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 3),
    four_bed_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 4),
    five_bed_90day: availabilityAvg(units90day, accessor, (unit) => unit.bed === 5)
  }
}

function computePropertyPayload(property: PropertyDetailsResponse, today: Date) {
  const daysAgo30 = addDays(today, -30)
  const daysAgo60 = addDays(today, -60)
  const daysAgo90 = addDays(today, -90)

  const units_30day = getUnitsInDateRange(property, daysAgo30, today)
  const units_60day = getUnitsInDateRange(property, daysAgo60, today)
  const units_90day = getUnitsInDateRange(property, daysAgo90, today)

  const lastOcc = occupancyOverTime(property, today, today).pop() || null
  const propertyOccupancy = lastOcc?.advertisedOccupancy ?? null
  const propertyExposure = lastOcc?.exposure ?? null

  return {
    building_name: property.building_name,
    lat: property.lat,
    lon: property.lon,
    census_tract_id: property.census_tract_id,
    full_address: formatFullAddress(property),
    overall_quality: property.building_quality.property_overall_quality,
    year_built: property.year_built,
    number_units: property.number_units,
    number_stories: property.number_stories,
    management_company: property.management_company,
    property_website: property.building_website,
    avg_sqft: avgSqft(property.building_availability),
    concessions_history: property.concessions_history,
    created_on: property.created_on,
    avg_rent_per_sqft: {
      all_30day: avgRentPerSqft(units_30day),
      all_60day: avgRentPerSqft(units_60day),
      all_90day: avgRentPerSqft(units_90day)
    },
    avg_eff_rent_per_sqft: {
      all_30day: avgEffRentPerSqft(units_30day),
      all_60day: avgEffRentPerSqft(units_60day),
      all_90day: avgEffRentPerSqft(units_90day)
    },
    building_amenities: property.building_amenities,
    unit_amenities: property.unit_amenities,
    reviews: normalizeReviews(property.review_analysis),
    pricing_strategy: property.pricing_strategy,
    occupancy: propertyOccupancy,
    exposure: propertyExposure,
    avg_sqft_units: computeAvgGrid(units_30day, units_60day, units_90day, (unit) => unit.sqft),
    avg_market_rent: computeAvgGrid(units_30day, units_60day, units_90day, (unit) => unit.price),
    avg_effective_rent: computeAvgGrid(
      units_30day,
      units_60day,
      units_90day,
      (unit) => unit.effective_price
    ),
    avg_market_rent_psf: computeAvgGrid(
      units_30day,
      units_60day,
      units_90day,
      (unit) => (unit.price !== null && unit.sqft !== null && unit.price / unit.sqft) || null
    ),
    avg_eff_rent_psf: computeAvgGrid(
      units_30day,
      units_60day,
      units_90day,
      (unit) =>
        (unit.effective_price !== null && unit.sqft !== null && unit.effective_price / unit.sqft) ||
        null
    ),
    finishes: {
      kitchen: qualityFromFloat(property.building_quality.avg_quality_score_kitchen),
      bathroom: qualityFromFloat(property.building_quality.avg_quality_score_bathroom),
      living_room: qualityFromFloat(property.building_quality.avg_quality_score_living_room),
      dining_room: qualityFromFloat(property.building_quality.avg_quality_score_dining_room),
      bedroom: qualityFromFloat(property.building_quality.avg_quality_score_bedroom),
      common_areas: qualityFromFloat(property.building_quality.avg_quality_score_common_areas),
      swimming_pool: qualityFromFloat(property.building_quality.avg_quality_score_swimming_pool),
      laundry_room: qualityFromFloat(property.building_quality.avg_quality_score_laundry_room),
      walk_in_closet: qualityFromFloat(property.building_quality.avg_quality_score_walk_in_closet),
      fitness_center: qualityFromFloat(property.building_quality.avg_quality_score_fitness_center)
    },
    fees: {
      cats_monthly_rent: property.cats_monthly_rent,
      cats_one_time_fee: property.cats_one_time_fee,
      cats_deposit: property.cats_deposit,
      dogs_monthly_rent: property.dogs_monthly_rent,
      dogs_one_time_fee: property.dogs_one_time_fee,
      dogs_deposit: property.dogs_deposit,
      admin_fee: property.admin_fee,
      amenity_fee: property.amenity_fee,
      application_fee: property.application_fee,
      storage_fee: property.storage_fee,
      parking_covered: property.parking_covered,
      parking_garage: property.parking_garage,
      parking_surface_lot: property.parking_surface_lot
    },
    estimated_unit_mix: estimateUnitMix(property, 'floorplan').map((_) => ({
      ..._,
      units: undefined
    }))
  }
}

function buildUnitsHistory(
  properties: PropertyDetailsResponse[],
  rentBasis: BestLeaseTermRentBasisValue = defaultBestLeaseTermRentBasis
) {
  const epoch = new Date(0)
  const today = new Date()

  const noLeaseTermFilter = new Set<number | null>()

  return properties.flatMap((prop) =>
    getLatestPricesByFilter(prop, epoch, today).map(({ unit, lastHistory, period }) => {
      const bestPlan = getBestLeaseTermPlan(lastHistory, noLeaseTermFilter, rentBasis)
      const leaseTerm = bestPlan?.[0] ?? lastHistory.lease_term ?? null
      const price = lastHistory.price
      const effectivePrice = lastHistory.effective_price

      return {
        ...unit,
        availability_periods: undefined,
        price_plans: undefined,
        history: undefined,
        enter_market: period.enter_market,
        exit_market: period.exit_market,
        days_on_market: period.days_on_market,
        availability: period.availability,
        deposit: period.deposit ?? null,
        price,
        min_price: lastHistory.min_price,
        max_price: lastHistory.max_price,
        effective_price: effectivePrice,
        min_effective_price: lastHistory.min_effective_price,
        max_effective_price: lastHistory.max_effective_price,
        lease_term: leaseTerm ?? null,
        rent_per_sqft: price !== null && unit.sqft !== null ? price / unit.sqft : null,
        effective_rent_per_sqft:
          effectivePrice !== null && unit.sqft !== null ? effectivePrice / unit.sqft : null,
        building_name: prop.building_name,
        street_address: prop.street_address,
        is_active: period.exit_market === null,
        enter_market_excel: period.enter_market ? excelDate(period.enter_market) : null,
        exit_market_excel: period.exit_market ? excelDate(period.exit_market) : null
      }
    })
  )
}

export const excelPayload = (
  similarities: {
    [key: string]: Similarities
  },
  subjectDetail: PropertyDetailsResponse | null,
  comparableDetails: PropertyDetailsResponse[],
  expenseBenchmarks: ExpenseBenchmarksResponse | null,
  rentBasis: BestLeaseTermRentBasisValue = defaultBestLeaseTermRentBasis
) => {
  if (Object.keys(similarities).length === 0) {
    return null
  }
  if (subjectDetail === null) {
    return null
  }
  if (comparableDetails.length === 0) {
    return null
  }

  const today = new Date()

  const updatedComparables = comparableDetails.map(withFloorplanNames)
  const updatedSubject = withFloorplanNames(subjectDetail)

  return {
    downloaded_at: excelDate(format(new Date(), 'yyyy-MM-dd')),
    subject: computePropertyPayload(updatedSubject, today),
    // we concatenate the subject units with the comparable building_availability
    units: updatedComparables
      .map((_) =>
        _.building_availability.map((unit) => ({
          ...unit,
          availability_periods: undefined,
          price_plans: undefined,
          history: undefined,
          rent_per_sqft:
            (unit.price !== null && unit.sqft !== null && unit.price / unit.sqft) || null,
          effective_rent_per_sqft:
            (unit.effective_price !== null &&
              unit.sqft !== null &&
              unit.effective_price / unit.sqft) ||
            null,
          building_name: _.building_name,
          street_address: _.street_address,
          is_active: unit.exit_market === null,
          enter_market_excel: unit.enter_market ? excelDate(unit.enter_market) : null,
          exit_market_excel: unit.exit_market ? excelDate(unit.exit_market) : null
        }))
      )
      .concat(
        updatedSubject.building_availability.map((unit) => ({
          ...unit,
          availability_periods: undefined,
          price_plans: undefined,
          history: undefined,
          rent_per_sqft:
            (unit.price !== null && unit.sqft !== null && unit.price / unit.sqft) || null,
          effective_rent_per_sqft:
            (unit.effective_price !== null &&
              unit.sqft !== null &&
              unit.effective_price / unit.sqft) ||
            null,
          building_name: updatedSubject.building_name,
          street_address: updatedSubject.street_address,
          is_active: unit.exit_market === null,
          enter_market_excel: unit.enter_market ? excelDate(unit.enter_market) : null,
          exit_market_excel: unit.exit_market ? excelDate(unit.exit_market) : null
        }))
      )
      .flat(),
    unitsHistory: buildUnitsHistory([...updatedComparables, updatedSubject], rentBasis),
    comparables: updatedComparables.map((_) => {
      const payload = computePropertyPayload(_, today)
      return {
        ...payload,
        similarity: similarities[_.id]?.overall ? similarities[_.id]?.overall : '-',
        distance: similarities[_.id]?.distance ? similarities[_.id]?.distance : '-',
        distance_miles: similarities[_.id]?.distance_miles
          ? similarities[_.id]?.distance_miles
          : '-'
      }
    }),
    financials:
      expenseBenchmarks === null
        ? null
        : {
            explanation_text:
              'Computed based on operating expense data from similar multifamily properties in your market, controlling for differences in size, age, quality, amenities and location.',
            gross_potential_rent: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.gross_potential_rent,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi || null,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            vacancy_loss: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.vacancy_loss,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent || null
            ),
            other_income: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.other_income,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            parking_income: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.parking_income,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            laundry_vending: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.laundry_vending_income,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            effective_gross_income: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.egi,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            real_estate_taxes: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.real_estate_taxes,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            property_insurance: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.property_insurance,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            utilities: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.utilities,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            repair_and_maintenance: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.repair_and_maintenance,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            management_fees: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.management_fees,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            payroll_and_benefits: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.payroll_and_benefits,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            marketing: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.advertising_and_marketing,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            professional_fees: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.professional_fees,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            general_and_administrative: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.general_and_administrative,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            other_expenses: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.other_expenses,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            total_operating_expenses: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.total_operating_expenses,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            NOI: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.noi,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            net_operating_income: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.noi,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            ),
            replacement_reserves: computeFinancialBreakdown(
              expenseBenchmarks.subject_estimate.replacement_reserves,
              subjectDetail.number_units,
              expenseBenchmarks.subject_estimate.egi,
              expenseBenchmarks.subject_estimate.gross_potential_rent
            )
          }
  }
}
